import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { HiArrowRight, HiChevronLeft, HiChevronRight } from 'react-icons/hi';

import { getProducts, trackEvent } from '../backend/utils/orderUtils';
import { mapDbProduct } from '../utils/productMap';
/* Aliased so the shop's own Supabase `trackEvent` above and Google
   Analytics can both fire from the same handlers. */
import {
  trackAddToCart as gaAddToCart,
  trackSelectItem as gaSelectItem,
  trackViewItem as gaViewItem,
  trackViewItemList as gaViewItemList,
} from '../utils/analytics';
import useSectionView from '../utils/useSectionView';
import { useCart } from '../context/CartContext';
import ProductCard from './ProductCard';
import ProductModal from './ProductModal';
import ImageLightbox from './ImageLightbox';

import '../styles/FeaturedProducts.css';

/* How many stones are pulled from the catalogue to draw on. */
const POOL_SIZE = 18;

/* How many sit in the rail at any moment. Four fit a desktop screen,
   so six leaves a little to swipe to. */
const WINDOW_SIZE = 6;

/** How long a set stays up before the next one takes its place. */
const SWAP_MS = 15000;

/** Length of the crossfade between sets. */
const FADE_MS = 320;

/* After someone swipes or taps an arrow, leave the shelf alone for a
   while. Swapping the cards out from under someone mid-browse is the
   whole reason auto-rotating sections get a bad name. */
const COOLDOWN_MS = 20000;

/* Skeleton tiles while the catalogue loads — one screenful, not the
   whole rail. */
const SKELETONS = 4;

/**
 * Real products, high on the home page.
 *
 * Someone landing on the site previously met a hero, a planet chart and
 * a consultation banner before anything purchasable — so it read as an
 * astrology service. This puts stones with prices and an Add to Bag
 * button above the fold-and-a-bit.
 *
 * Which products: whatever is ticked "Featured" in the admin product
 * form (is_featured), topped up with the best sellers by
 * purchase_count so the shelf is never short.
 *
 * The shelf shows WINDOW_SIZE of them at a time and trades the whole
 * set for the next one every SWAP_MS, crossfading between the two. So
 * a visitor who lingers is shown genuinely different stock rather than
 * the same handful of cards.
 */
export default function FeaturedProducts() {
  const [pool, setPool] = useState([]);
  const [loading, setLoading] = useState(true);

  const [detailProduct, setDetailProduct] = useState(null);
  const [zoomProduct, setZoomProduct] = useState(null);
  const [justAdded, setJustAdded] = useState(null);

  const railRef = useRef(null);
  const [windowIndex, setWindowIndex] = useState(0);
  const [fading, setFading] = useState(false);
  const [hovered, setHovered] = useState(false);
  /* Bumped on every deliberate interaction; the effect below reads it
     as "start the quiet period again". */
  const [interactions, setInteractions] = useState(0);
  const [cooling, setCooling] = useState(false);

  const { addItem } = useCart();
  const sectionRef = useSectionView('featured');

  const load = useCallback(async () => {
    const { success, products: rows } = await getProducts();

    if (!success || !rows?.length) {
      setPool([]);
      setLoading(false);
      return;
    }

    // Prefer in-stock — never lead the home page with something that
    // can't be bought — but fall back to everything rather than
    // rendering an empty row.
    const sellable = rows.filter((p) => p.in_stock !== false);
    const stock = sellable.length ? sellable : rows;

    const featured = stock.filter((p) => p.is_featured);

    // Top up with the best sellers so the shelf is always full. Ticking
    // one product Featured shouldn't leave five empty slots.
    const bestsellers = [...stock].sort(
      (a, b) => (b.purchase_count || 0) - (a.purchase_count || 0)
    );

    const chosen = [];
    const seen = new Set();
    for (const p of [...featured, ...bestsellers]) {
      if (seen.has(p.id)) continue;
      seen.add(p.id);
      chosen.push(p);
      if (chosen.length === POOL_SIZE) break;
    }

    setPool(chosen.map(mapDbProduct));
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    if (!justAdded) return;
    const t = setTimeout(() => setJustAdded(null), 1800);
    return () => clearTimeout(t);
  }, [justAdded]);

  /* ---------------------- the shelf ---------------------- */

  /* Someone who has asked their system to cut animation gets a still
     shelf: no swapping, and the arrows jump rather than glide. */
  const reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

  // Nothing to trade for if the catalogue barely fills one set.
  const windowCount =
    pool.length > WINDOW_SIZE ? Math.ceil(pool.length / WINDOW_SIZE) : 1;

  /* The set on show. Wrapping with a modulo rather than slicing means
     the last set is always full — a catalogue of 8 gives 6 + 6 rather
     than 6 + a lonely 2. WINDOW_SIZE <= pool.length here, so a set
     can't repeat a stone. */
  const shown = useMemo(() => {
    if (pool.length <= WINDOW_SIZE) return pool;

    const start = (windowIndex * WINDOW_SIZE) % pool.length;
    return Array.from(
      { length: WINDOW_SIZE },
      (_, i) => pool[(start + i) % pool.length]
    );
  }, [pool, windowIndex]);

  const paused =
    hovered || cooling || !!detailProduct || !!zoomProduct || reduceMotion;

  /* Fade out, change the cards while they can't be seen, fade back in.
     The rail element itself is never unmounted — swapping it out would
     collapse the section's height for a frame and jolt the page. */
  const swapTimer = useRef(null);

  useEffect(() => {
    if (paused || windowCount < 2) return;

    const tick = setInterval(() => {
      setFading(true);

      swapTimer.current = setTimeout(() => {
        setWindowIndex((i) => (i + 1) % windowCount);
        // Back to the left edge, done while the cards are invisible.
        if (railRef.current) railRef.current.scrollLeft = 0;
        setFading(false);
      }, FADE_MS);
    }, SWAP_MS);

    return () => {
      clearInterval(tick);
      clearTimeout(swapTimer.current);
    };
  }, [paused, windowCount]);

  // Leave a half-finished fade behind and the shelf stays blank.
  useEffect(() => () => clearTimeout(swapTimer.current), []);

  /* Quiet period after a swipe, a wheel, or an arrow. Re-running on
     every interaction restarts the clock rather than stacking timers. */
  useEffect(() => {
    if (interactions === 0) return;

    setCooling(true);
    const t = setTimeout(() => setCooling(false), COOLDOWN_MS);
    return () => clearTimeout(t);
  }, [interactions]);

  /* Wheel and touch are unambiguous human input — unlike `scroll`,
     which the scrollTo below fires too. */
  const takeOver = () => setInteractions((n) => n + 1);

  const step = (direction) => {
    takeOver();

    const rail = railRef.current;
    if (!rail) return;

    const furthest = rail.scrollWidth - rail.clientWidth;
    const next = rail.scrollLeft + direction * rail.clientWidth;

    rail.scrollTo({
      left: Math.max(0, Math.min(next, furthest)),
      behavior: reduceMotion ? 'auto' : 'smooth',
    });
  };

  /* One impression per set, not one per swap — a page left open for
     ten minutes shouldn't file the same event forty times. */
  const reported = useRef(new Set());

  useEffect(() => {
    if (loading || shown.length === 0) return;
    if (reported.current.has(windowIndex)) return;

    reported.current.add(windowIndex);
    gaViewItemList('Home — Featured', shown);
  }, [loading, shown, windowIndex]);

  const openDetails = (product) => {
    setDetailProduct(product);
    gaSelectItem(product, 'Home — Featured');
    gaViewItem(product);
  };

  const handleAddToCart = (product) => {
    addItem(product, 1);
    setJustAdded({ id: product.id, at: Date.now() });
    trackEvent({
      eventType: 'add_to_cart',
      productId: product.supabaseId,
    }).catch(() => {});
    gaAddToCart(product, 1, 'Home — Featured');
  };

  // A shop with nothing in it shouldn't render an empty band.
  if (!loading && pool.length === 0) return null;

  return (
    <section className="featured" id="featured" ref={sectionRef}>
      <div className="featured-inner">
        <motion.div
          className="featured-head"
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          
          <h2 className="featured-title">Discover Something Special</h2>
          <p className="featured-subtitle">
            Lab-certified gemstones, energised and ready to ship across India.
          </p>
        </motion.div>

        {loading ? (
          <div className="featured-rail">
            {Array.from({ length: SKELETONS }).map((_, i) => (
              <div className="featured-skeleton" key={i} />
            ))}
          </div>
        ) : (
          <div
            className="featured-railwrap"
            onMouseEnter={() => setHovered(true)}
            onMouseLeave={() => setHovered(false)}
            onFocus={() => setHovered(true)}
            onBlur={() => setHovered(false)}
          >
            <div
              className={`featured-rail ${fading ? 'is-fading' : ''}`}
              ref={railRef}
              onWheel={takeOver}
              onTouchStart={takeOver}
              role="region"
              aria-label="Featured stones"
              /* The set changes on its own, so a screen reader should
                 be told rather than left on stale cards. */
              aria-live="polite"
            >
              {shown.map((product) => (
                <ProductCard
                  key={product.id}
                  product={product}
                  justAdded={justAdded}
                  onZoom={setZoomProduct}
                  onOpenDetails={openDetails}
                  onAddToCart={handleAddToCart}
                  compact
                />
              ))}
            </div>

            {/* Pointer devices can't swipe. Hidden on touch and on
                narrow screens, where the rail is already draggable. */}
            <button
              type="button"
              className="featured-arrow is-prev"
              onClick={() => step(-1)}
              aria-label="Previous stones"
            >
              <HiChevronLeft />
            </button>
            <button
              type="button"
              className="featured-arrow is-next"
              onClick={() => step(1)}
              aria-label="Next stones"
            >
              <HiChevronRight />
            </button>
          </div>
        )}

        <div className="featured-cta">
          <Link to="/shop" className="featured-btn">
            <span>View All Products</span>
            <HiArrowRight />
          </Link>
        </div>
      </div>

      {detailProduct && (
        <ProductModal
          product={detailProduct}
          onAddToCart={handleAddToCart}
          onClose={() => setDetailProduct(null)}
        />
      )}

      {zoomProduct && (
        <ImageLightbox
          src={zoomProduct.image}
          alt={zoomProduct.name}
          onClose={() => setZoomProduct(null)}
        />
      )}
    </section>
  );
}
